import type { BaseSyntheticEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	useMultiStyleConfig,
} from "@chakra-ui/react";
import type { ShareClassTranslationMap } from "@/types/ShareClassTranslationMap";
import { AccessibleModal } from "../AccessibleModal/AccessibleModal";
import DownloadAllocationModalBodyStageOne from "./Stages/StageOne/DownloadAllocationModalBodyStageOne";
import DownloadAllocationModalBodyStageTwo from "./Stages/StageTwo/DownloadAllocationModalBodyStageTwo";
import type { EtfHoldingsProps } from "../[Pages]/FundDetailPage/mapping/FxEtfHoldingsPanel";
import { envVar } from "@/services/env";
import { stringify } from "qs";

type DownloadAllocationModalProps = {
	isOpen: boolean;
	onClose: () => void;
	variant?: string;
	size?: string;
	translations: ShareClassTranslationMap;
} & EtfHoldingsProps['downloadAllocationModal'];

export interface DocumentData {
	shareClassExternalId: string;
	shareClassName: string | string[];
	supportedFileTypes: string[];
	params?: Record<string, string> | undefined;
}

const MAX_STAGES = 2;

function DownloadAllocationModal({ isOpen, stages, onClose, documentData, translations, variant, size }: DownloadAllocationModalProps) {
	const styleParts = useMultiStyleConfig("DownloadPriceModal", { variant, size });
	const [isGeneratingData, setIsGeneratingData] = useState<boolean>(false);
	const [modalStage, setModalStage] = useState(0);
	const [selectedFileType, setSelectedFileType] = useState<string | undefined>(undefined);
	const [canGenerateData, setCanGenerateData] = useState(true);
	const [fullSizeModal, setFullSizeModal] = useState(false);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		setFullSizeModal(window.innerWidth < 520);
		reset();
	}, [isOpen]);

	const generateData = useCallback(() => {
		setIsGeneratingData(true);
		setTimeout(() => {
			setIsGeneratingData(false);
			setModalStage(1);
		}, 3000);
	}, []);

	const reset = () => {
		setModalStage(0);
		setSelectedFileType(undefined);
	};

	const handleDownload = useCallback(async () => {
		const data = documentData;
		const passData = data.params ? { ...data.params } : {};

		const url = `${envVar("PUBLIC_ENV__API_HOST")}/api/v1/getShareClassAllocationDownload?${stringify({
			...passData,
			shareclassExternalId: documentData.shareClassExternalId,
			fileType: selectedFileType,
			allocationType: 'etfHoldings'
		})}`;

		window.open(url);

		onClose();
		reset();
	}, [selectedFileType, onClose, documentData]);

	const handleStageChange = useCallback(
		(e: BaseSyntheticEvent) => {
			const stage = e.currentTarget.dataset.stage === "prev" ? -1 : 1;

			setModalStage((prev) => {
				const temp = prev + stage;
				if (temp == 1) {
					if (canGenerateData) {
						generateData();
						return prev;
					}
				}
				if (temp < 0 || temp >= MAX_STAGES) {
					return prev;
				}
				return temp;
			});
		},
		[generateData, canGenerateData]
	);

	const handleFileTypeChange = useCallback((fileType: string) => {
		setSelectedFileType(fileType);
	}, []);

	const renderModalBody = useMemo(() => {
		switch (modalStage) {
			case 0:
				return (
					<DownloadAllocationModalBodyStageOne
						supportedFileTypes={documentData.supportedFileTypes}
						selectedFileType={selectedFileType}
						handleFileTypeChange={handleFileTypeChange}
						translations={translations}
						{...stages.one}
					/>
				);
			default:
				return (
					<DownloadAllocationModalBodyStageTwo
						shareClassName={documentData.shareClassName}
						translations={translations}
						{...stages.two}
					/>
				);
		}
	}, [modalStage, documentData, selectedFileType, handleFileTypeChange, translations, stages]);

	const modalHeaderText = useMemo(() => {
		switch (modalStage) {
			case 0:
				return stages.one.title;
			default:
				return stages.two.title;
		}
	}, [modalStage, stages]);

	const isNextDisabled = useMemo(() => {
		switch (modalStage) {
			case 0:
				return selectedFileType === undefined || isGeneratingData;
			default:
				return modalStage + 1 > MAX_STAGES;
		}
	}, [modalStage, selectedFileType, isGeneratingData]);

	const nextButtonText = useMemo(() => {
		switch (modalStage) {
			case 0: {
				if (isGeneratingData) {
					return <Spinner />;
				}
				return translations?.next;
			}
			default:
				return translations?.next;
		}
	}, [modalStage, isGeneratingData, translations]);

	if (!translations) {
		return null;
	}

	return (
		<AccessibleModal id="download-allocation-modal" isOpen={isOpen} onClose={onClose} isCentered size={fullSizeModal ? "full" : undefined}>
			<ModalOverlay />
			<ModalContent sx={styleParts.modalContent} data-testid="DownloadPriceModal">
				<ModalCloseButton sx={styleParts.modalCloseButton} color="shade.100" size="xl" />
				<ModalHeader sx={styleParts.modalHeader}>
					<Text sx={styleParts.modalHeaderTitle}>{modalHeaderText}</Text>
					<Text sx={styleParts.modalHeaderStageText}>{`${translations.step} ${modalStage + 1} ${translations.of
						} ${MAX_STAGES}`}</Text>
				</ModalHeader>
				<ModalBody sx={styleParts.modalBody}>{renderModalBody}</ModalBody>
				<ModalFooter sx={styleParts.modalFooter} data-testid="DownloadPriceModal_Footer">
					<Button sx={styleParts.previousButton} data-stage="prev" onClick={handleStageChange} isDisabled={modalStage - 1 < 0}>
						{translations.back}
					</Button>
					{modalStage + 1 < MAX_STAGES ? (
						<Button sx={styleParts.nextButton} data-stage="next" onClick={handleStageChange} isDisabled={isNextDisabled}>
							{nextButtonText}
						</Button>
					) : (
						<Button sx={styleParts.nextButton} onClick={handleDownload}>
							{stages.two.downloadButtonLabel}
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</AccessibleModal>
	);
}

export default DownloadAllocationModal;
